import {CurrentUserClass} from 'shared/CurrentUser';
import globalEventBus, {GLOBAL_EVENTS} from 'shared/globalEventBus';
import defineSection from '~/spa/section';
import sectionHead from '~/spa/head';
import {getWikiChildren} from '../wiki_page/WikiChildrenPages';
import {COMMUNITY_EVENT_ROUTER_PREFIX, COMMUNITY_WIKI_ROUTER_PREFIX} from './config';
import {
    getEventDetailPageChildren,
    getEventDetailPageRecords,
    EVENT_NAME_PREFIX_META
} from '../events/new_event/eventDetailPageChildren';

// which of the two the community's event pages use depends on the viewer, and the
// unauthenticated one is not simply the other with pieces hidden - it is a
// different component on a different path
const getEventRoutes = () => {
    if (new CurrentUserClass().is_authenticated()) {
        return getEventDetailPageRecords(
            COMMUNITY_EVENT_ROUTER_PREFIX,
            ({pathTail, namePrefix, nameSuffix}) => ({
                name: 'community-events-event' + nameSuffix,
                path: 'events/:eventId(\\d+)/' + pathTail,
                component: () => import(/* webpackChunkName: "eventDetail" */ '../events/new_event/EventDetailPage'),
                props: true,
                children: getEventDetailPageChildren(namePrefix),
                meta: {name: 'events-event', [EVENT_NAME_PREFIX_META]: namePrefix}
            })
        );
    }
    return [{
        name: 'community-events-event-unauth',
        // router 4 has no unnamed groups: the optional trailing 'info' the path used to
        // spell as `(info)?` is a named param constrained to that one value
        path: 'events/:eventId(\\d+)/:date(\\d{4}-\\d{2}-\\d{2})?/:page(info)?',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageEventsUnauthView.vue'),
        props: true,
        meta: {name: 'events-event-unauth'}
    }];
};

const COMMUNITY_PAGE_CHILDREN = [
    {
        name: 'community-info',
        path: '',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageAboutUs.vue'),
        props: true, meta: {name: 'info'}
    },
    {
        name: 'community-stories',
        path: 'stories',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageStories.vue'),
        props: true,
        meta: {name: 'stories'}
    },
    {
        name: 'community-stories-story',
        path: 'stories/:storySlug',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageStoriesStory.vue'),
        props: true,
        meta: {name: 'stories-story'}
    },
    {
        name: 'community-events',
        path: 'events',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageEvents.vue'),
        props: true,
        meta: {name: 'events'}
    },
    ...getEventRoutes(),
    {
        name: 'community-groups',
        path: 'groups',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageGroups.vue'),
        props: true,
        meta: {name: 'groups'}
    },
    {
        name: 'community-settings',
        path: 'settings',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageSettings.vue'),
        props: true,
        meta: {name: 'settings'}
    },
    {
        name: 'community-wiki',
        path: 'wiki',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageWiki.vue'),
        props: true,
        meta: {name: 'wiki'},
        children: getWikiChildren(COMMUNITY_WIKI_ROUTER_PREFIX)
    },
    {
        name: 'community-wiki-access-page',
        path: 'wiki-access-page',
        component: () => import(/* webpackChunkName: "community" */ './CommunityPageManageWikiAccess.vue'),
        props: true,
        meta: {name: 'wiki-access-page'}
    },
];

const setup = async () => {
    // the community itself is not seeded here: setup runs once for the section,
    // and the section routes between communities. CommunityPage.vue fetches the
    // one its slug names, and again when the slug changes

    if (!new CurrentUserClass().is_authenticated()) {
        // deferred so the header has subscribed by the time the event goes out
        setTimeout(() => {
            globalEventBus.emit(GLOBAL_EVENTS.ENABLE_FOOTER_LOGIN_MODE);
        }, 1);
    }
};

export default defineSection({
    // the favicon and the title template; CommunityPage names the community itself
    head: sectionHead(),
    setup,
    routes: [
        {
            path: '/community/:communitySlug/',
            component: () => import(/* webpackChunkName: "community" */ './CommunityPage.vue'),
            props: true,
            children: COMMUNITY_PAGE_CHILDREN
        },
    ]
});
